import { t } from "@lingui/core/macro";
import { Button } from "@rakazo/ui-web";
import { ExternalLink, MonitorPlay } from "lucide-react";
import { type RefObject, useEffect, useRef, useState } from "react";
import type { ArtifactTarget } from "../lib/artifact-open";
import { useSealedPreview } from "../lib/preview-artifact";
import { usePreviewPanel } from "../lib/preview-panel";

/** Result of the HEAD check shown on the card. */
export type PreviewReachability = "checking" | "reachable" | "gone" | "unreachable";

/** HEAD on the preview URL (PIV-31, interface 2): 200 reachable, 410/502 gone. */
export async function checkPreviewReachable(
  url: string,
  signal?: AbortSignal,
): Promise<PreviewReachability> {
  try {
    const response = await fetch(url, {
      method: "HEAD",
      credentials: "include",
      cache: "no-store",
      redirect: "manual",
      signal,
    });
    if (response.status === 200) return "reachable";
    if (response.status === 410 || response.status === 502) return "gone";
    return "unreachable";
  } catch {
    if (signal?.aborted) return "checking";
    return "unreachable";
  }
}

/** True while the element is in or close to the viewport; true without IntersectionObserver. */
function useNearViewport(element: RefObject<HTMLElement | null>): boolean {
  const [near, setNear] = useState(true);
  useEffect(() => {
    const node = element.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (entry) setNear(entry.isIntersecting);
      },
      { rootMargin: "320px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [element]);
  return near;
}

type PreviewCardProps = {
  target: ArtifactTarget;
  artifactId: string;
  /** Artifact name, shown until the stored title has loaded. */
  name: string;
};

/**
 * A preview artifact (kind "file" with PREVIEW_ARTIFACT_MIME_TYPE, PIV-32). The
 * sealed URL comes from artifacts.get and is read again before every HEAD. It
 * is kept fresh only while the card is in or near the viewport, so a long chat
 * with many previews does not keep minting tokens it never uses.
 */
export function PreviewCard({ target, artifactId, name }: PreviewCardProps) {
  const panel = usePreviewPanel();
  const openButton = useRef<HTMLButtonElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const visible = useNearViewport(container);
  const preview = useSealedPreview(target, artifactId, visible);
  const { refresh } = preview;
  const [status, setStatus] = useState<PreviewReachability>("checking");
  const checked = useRef(false);
  const view = preview.view;
  const url = view?.url ?? null;
  const loaded = preview.status === "ready" || preview.status === "failed";

  useEffect(() => {
    if (!loaded || checked.current) return;
    checked.current = true;
    const controller = new AbortController();
    async function check(first: string | null): Promise<PreviewReachability> {
      if (!first) return "unreachable";
      const result = await checkPreviewReachable(first, controller.signal);
      if (result !== "gone") return result;
      // 410 can mean only the URL expired: read a fresh one and ask once more.
      const fresh = await refresh();
      return fresh?.url ? checkPreviewReachable(fresh.url, controller.signal) : result;
    }
    void check(url).then((next) => {
      if (!controller.signal.aborted) setStatus(next);
    });
    return () => {
      controller.abort();
      checked.current = false;
    };
  }, [loaded, refresh]);

  const title = view?.title ?? name;
  const description = view?.description ?? null;
  const isOpen = panel?.source?.key === artifactId;
  const canOpen = url !== null && (status === "reachable" || status === "checking");
  const heading = t`Preview: ${title}`;

  return (
    <div
      ref={container}
      data-testid="preview-card"
      data-status={status}
      className="flex w-[min(100%,360px)] flex-col gap-3 rounded-2xl border border-border bg-card px-4 py-3.5 text-left text-foreground"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <MonitorPlay className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14.5px] font-medium" title={heading}>
            {heading}
          </div>
          <div
            className="mt-0.5 flex items-start gap-1.5 text-[13px] text-muted-foreground"
            role="status"
          >
            <span
              aria-hidden="true"
              className={`mt-[7px] size-1.5 shrink-0 rounded-full ${
                status === "reachable"
                  ? "bg-success"
                  : status === "checking"
                    ? "bg-muted-foreground/50"
                    : "bg-destructive"
              }`}
            />
            <span>
              {status === "reachable"
                ? t`Reachable`
                : status === "checking"
                  ? t`Checking…`
                  : status === "gone"
                    ? t`No longer reachable – the bot needs to restart the server`
                    : t`Not reachable`}
            </span>
          </div>
          <div className="mt-0.5 text-[12px] text-muted-foreground/80">
            {t`Runs without localStorage and without cookies from the preview server`}
          </div>
          {description ? (
            <div className="mt-1.5 line-clamp-2 text-[13px] text-muted-foreground">
              {description}
            </div>
          ) : null}
        </div>
      </div>
      {canOpen ? (
        <div className="flex items-center gap-2">
          <Button
            ref={openButton}
            size="lg"
            aria-pressed={panel ? isOpen : undefined}
            onClick={() => {
              if (panel) {
                panel.open(
                  { kind: "preview", key: artifactId, title, target, artifactId },
                  openButton.current,
                );
              } else {
                if (url) window.open(url, "_blank", "noopener,noreferrer");
              }
            }}
          >
            {t`Open`}
          </Button>
          <a
            href={url ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 text-[13.5px] font-medium hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
          >
            <ExternalLink className="size-3.5" aria-hidden="true" />
            {t`Open in new tab`}
          </a>
        </div>
      ) : null}
    </div>
  );
}
